import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth, HOME_FOR } from "@/context/AuthContext";
import { LayoutDashboard, Menu, X, Linkedin, Twitter, Facebook, Instagram, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UserMenu } from "@/components/UserMenu";
import { GPA_LOGO, LSC_LOGO } from "@/lib/assets";

const links = [
  { to: "/", label: "Home" },
  { to: "/framework", label: "The Framework" },
  { to: "/how-it-works", label: "How TSB Works" },
  { to: "/research", label: "Research" },
  { to: "/audiences", label: "For You" },
  { to: "/faq", label: "FAQ" },
];

export function MarketingNav() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const dashHref = HOME_FOR[user?.role] || "/login";
  return (
    <header className="fixed inset-x-0 top-0 z-50 glass-light">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-4" data-testid="nav-logo">
          <img src={GPA_LOGO} alt="GPA+ — Beyond Grades. Beyond Limits. Beyond Ready." className="h-14 w-auto bg-transparent sm:h-16" />
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                pathname === l.to ? "bg-[#003057]/8 text-[#003057]" : "text-slate-600 hover:text-[#003057]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-2 md:flex">
          <Link to={dashHref}>
            <Button data-testid="nav-dashboard" className="rounded-full bg-[#78be21] px-5 font-semibold text-[#00243f] hover:bg-[#6aac1c]">
              <LayoutDashboard className="mr-2 h-4 w-4" /> My Dashboard
            </Button>
          </Link>
          <UserMenu />
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <UserMenu />
          <button onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle">
            {open ? <X className="h-6 w-6 text-[#003057]" /> : <Menu className="h-6 w-6 text-[#003057]" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block rounded-lg px-3 py-2 text-sm font-semibold text-slate-700">
              {l.label}
            </Link>
          ))}
          <Link to={dashHref} onClick={() => setOpen(false)}>
            <Button className="mt-2 w-full rounded-full bg-[#78be21] font-semibold text-[#00243f]">My Dashboard</Button>
          </Link>
        </div>
      )}
    </header>
  );
}

export function MarketingFooter() {
  const { user } = useAuth();
  const jobsHref = user?.role === "employer" ? "/app/employer/jobs" : "/app/student/jobs";
  return (
    <footer className="border-t border-slate-800 bg-[#00243f] text-slate-300">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-8 md:flex-row">
          <div className="max-w-sm">
            <div className="inline-flex items-center gap-4 rounded-lg bg-white px-4 py-3">
              <img src={GPA_LOGO} alt="GPA+" className="h-9 w-auto" />
              <span className="h-8 w-px bg-slate-300" />
              <img src={LSC_LOGO} alt="Lone Star College" className="h-8 w-auto" />
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              A Transferable Skills Benchmark for Lone Star College — measuring the durable skills that
              coursework builds and careers demand. Prototype on mock data, architected for D2L/Brightspace.
            </p>
            <div className="mt-5 flex items-center gap-3" data-testid="footer-social">
              {[
                { Icon: Linkedin, label: "LinkedIn", href: "#" },
                { Icon: Twitter, label: "X", href: "#" },
                { Icon: Facebook, label: "Facebook", href: "#" },
                { Icon: Instagram, label: "Instagram", href: "#" },
                { Icon: Youtube, label: "YouTube", href: "#" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 text-slate-400 transition-colors hover:border-[#78be21] hover:bg-[#78be21] hover:text-[#00243f]"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-10 text-sm sm:grid-cols-3">
            <div>
              <p className="font-semibold text-white">Audiences</p>
              <Link to="/for-students" className="mt-3 block text-slate-400 hover:text-[#78be21]">For Students</Link>
              <Link to="/for-employers" className="mt-2 block text-slate-400 hover:text-[#78be21]">For Employers</Link>
              <Link to="/for-educators" className="mt-2 block text-slate-400 hover:text-[#78be21]">For Educators</Link>
              <Link to="/for-administrators" className="mt-2 block text-slate-400 hover:text-[#78be21]">For Administrators</Link>
              <Link to="/supporters" className="mt-2 block text-slate-400 hover:text-[#78be21]">Supporters & Partners</Link>
            </div>
            <div>
              <p className="font-semibold text-white">Explore</p>
              <Link to="/framework" className="mt-3 block text-slate-400 hover:text-[#78be21]">The Framework</Link>
              <Link to="/how-it-works" className="mt-2 block text-slate-400 hover:text-[#78be21]">How TSB Works</Link>
              <Link to={jobsHref} className="mt-2 block text-slate-400 hover:text-[#78be21]">Job Board</Link>
              <Link to="/research" className="mt-2 block text-slate-400 hover:text-[#78be21]">Research & Methodology</Link>
              <Link to="/audiences" className="mt-2 block text-slate-400 hover:text-[#78be21]">For You</Link>
              <Link to="/faq" className="mt-2 block text-slate-400 hover:text-[#78be21]">FAQ</Link>
            </div>
            <div>
              <p className="font-semibold text-white">Demo</p>
              <Link to="/login" className="mt-3 block text-slate-400 hover:text-[#78be21]">Sign in</Link>
              <Link to="/privacy" className="mt-2 block text-slate-400 hover:text-[#78be21]">Privacy & Compliance</Link>
              <Link to="/data-sources" className="mt-2 block text-slate-400 hover:text-[#78be21]">Data & Attribution</Link>
              <Link to="/career-explorer" className="mt-2 block text-slate-400 hover:text-[#78be21]">Career Explorer</Link>
              <span className="mt-2 block text-slate-500">FERPA-aligned · RBAC</span>
            </div>
          </div>
        </div>
        <div className="mt-10 border-t border-slate-800 pt-6 text-xs text-slate-500">
          Prototype for leadership review. Career/program profiles are curated-from-research samples
          (O*NET / America Succeeds durable-skills data), not an official LSC catalog. No real student PII.
        </div>
      </div>
    </footer>
  );
}
